"use client";

import { startTransition, useEffect, useMemo, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import type { PageSearchParams } from "types/page";

import { getProductSearchHref } from "@features/product/utils/getProductSearchHref";

import { findCombination } from "../utils/findCombination";
import { findCombinationByPartNo } from "../utils/findCombinationByPartNo";
import { getVariantOptions } from "../utils/getVariantOptions";
import { resolveSelection } from "../utils/resolveSelection";
import type { VariantCatalog } from "../types";

type PendingSelection = {
  partNo: string | null;
  values: string[];
};

function readPartNo(searchParams?: PageSearchParams) {
  const value = searchParams?.variant;
  return Array.isArray(value) ? value[0] : value;
}

export function useVariantSelector(
  catalog: VariantCatalog,
  locale: string,
  searchParams?: PageSearchParams,
) {
  const router = useRouter();
  const pathname = usePathname();
  const [pending, setPending] = useState<PendingSelection | null>(null);
  const activePartNo = readPartNo(searchParams) ?? catalog.selectedPartNo;
  const optimistic =
    pending && pending.partNo !== catalog.selectedPartNo ? pending : null;

  useEffect(() => {
    function clearOptimisticSelection() {
      setPending(null);
    }

    window.addEventListener("popstate", clearOptimisticSelection);
    return () =>
      window.removeEventListener("popstate", clearOptimisticSelection);
  }, []);

  const values = useMemo(() => {
    if (optimistic) return optimistic.values;
    return (
      findCombinationByPartNo(catalog, activePartNo)?.values ??
      findCombinationByPartNo(catalog, catalog.selectedPartNo)?.values ??
      catalog.combinations[0]?.values ??
      []
    );
  }, [activePartNo, catalog, optimistic]);

  const attributes = useMemo(
    () =>
      catalog.attributes.map((attribute, index) => ({
        code: attribute.code,
        label: attribute.label,
        value: values[index] ?? "",
        options: getVariantOptions(catalog, values, index, locale),
      })),
    [catalog, locale, values],
  );

  function onSelect(index: number, value: string) {
    if (values[index] === value) return;

    const next = resolveSelection(catalog, values, index, value);
    const combination = findCombination(catalog, next);

    if (!combination) {
      setPending({ partNo: null, values: next });
      return;
    }

    if (combination.partNo === activePartNo) {
      setPending(null);
      return;
    }

    setPending({
      partNo: combination.partNo,
      values: combination.values,
    });

    const href = getProductSearchHref(searchParams, {
      variant: combination.partNo,
    });

    startTransition(() => {
      router.push(`${pathname}${href}`, { scroll: false });
    });
  }

  return { attributes, onSelect };
}
